import jsPDF from "jspdf";
import {
  beginBrandedPdf,
  drawPdfBrandFooter,
  drawPdfBrandLogoOnly,
} from "./pdfBrand";

const AMOUNT_BLANK = "______________________________";

/** Lower ~38% of A4 reserved for the tear-off admin office copy. */
const ADMIN_ZONE_RATIO = 0.62;

const formatAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return AMOUNT_BLANK;
  return `PKR ${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value) => {
  if (!value) return "____ / ____ / ________";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

function drawDetailRows(doc, marginX, contentW, startY, rows, rowGap = 7.5) {
  let y = startY;
  doc.setFontSize(10);
  for (const [label, value] of rows) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(64, 59, 85);
    doc.text(label, marginX, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(48, 45, 69);
    const lines = doc.splitTextToSize(String(value), contentW - 48);
    doc.text(lines, marginX + 48, y);
    y += Math.max(rowGap, lines.length * 4.8 + 2.5);
  }
  return y;
}

function drawWrappedParagraph(
  doc,
  marginX,
  contentW,
  startY,
  text,
  { fontSize = 9, lineH = 5.6, afterGap = 3 } = {},
) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(64, 59, 85);
  const lines = doc.splitTextToSize(text, contentW);
  lines.forEach((line, index) => {
    doc.text(line, marginX, startY + index * lineH);
  });
  return startY + lines.length * lineH + afterGap;
}

/** Signature + date lines only (name already appears in details / declaration). */
function drawSignatureBlock(doc, {
  marginX,
  contentW,
  pageW,
  startY,
  leftLabel,
  rightLabel,
  lineGap = 11,
}) {
  let y = startY;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(64, 59, 85);
  doc.text(leftLabel, marginX, y);
  doc.text(rightLabel, marginX + contentW / 2 + 6, y);

  y += 2;
  doc.setDrawColor(48, 45, 69);
  doc.setLineWidth(0.45);
  doc.line(marginX, y + lineGap, marginX + contentW / 2 - 10, y + lineGap);
  doc.line(
    marginX + contentW / 2 + 6,
    y + lineGap,
    pageW - marginX,
    y + lineGap,
  );
  return y + lineGap + 5;
}

/** Title on the left, copy label (e.g. TEACHER'S COPY) right-aligned on the same line. */
function drawTitleWithCopyLabel(doc, {
  marginX,
  pageW,
  y,
  title,
  copyLabel,
  titleSize = 12.5,
}) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(titleSize);
  doc.setTextColor(48, 45, 69);
  doc.text(title, marginX, y);

  doc.setFontSize(8);
  doc.setTextColor(122, 117, 139);
  doc.text(copyLabel.toUpperCase(), pageW - marginX, y, { align: "right" });
}

function drawSectionRule(doc, marginX, pageW, y) {
  doc.setDrawColor(229, 225, 239);
  doc.setLineWidth(0.35);
  doc.line(marginX, y, pageW - marginX, y);
  return y + 6;
}

/**
 * Compact party row for admin copy: printed name + signature line + date line.
 */
function drawAdminCopyPartyRow(doc, {
  marginX,
  contentW,
  startY,
  roleLabel,
  personName,
}) {
  const name = personName || "________________________";
  const colSig = marginX + contentW * 0.4;
  const colDate = marginX + contentW * 0.74;
  let y = startY;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(64, 59, 85);
  doc.text(`${roleLabel}:`, marginX, y);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(48, 45, 69);
  doc.text(name, marginX + 20, y, { maxWidth: contentW * 0.32 });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text("Signature", colSig, y);
  doc.text("Date", colDate, y);

  const lineY = y + 9;
  doc.setDrawColor(48, 45, 69);
  doc.setLineWidth(0.4);
  doc.line(colSig, lineY, colDate - 5, lineY);
  doc.line(colDate, lineY, marginX + contentW, lineY);

  return lineY + 6.5;
}

function drawCutSeparator(doc, marginX, pageW, y) {
  doc.setDrawColor(160, 155, 180);
  doc.setLineWidth(0.35);
  doc.setLineDashPattern([1.2, 1.2], 0);
  doc.line(marginX, y, pageW - marginX, y);
  doc.setLineDashPattern([], 0);
  return y + 5;
}

/**
 * Compact tear-off admin office copy (no declaration paragraphs).
 */
function drawAdminOfficeCopy(doc, {
  marginX,
  contentW,
  pageW,
  startY,
  brandAssets,
  title,
  teacherName,
  adminName,
  amount,
  entryDate,
  description,
  recordLabel,
}) {
  let y = drawPdfBrandLogoOnly(doc, brandAssets, {
    startY,
    maxH: 8.5,
    gapAfter: 3.5,
  });

  drawTitleWithCopyLabel(doc, {
    marginX,
    pageW,
    y,
    title,
    copyLabel: "Admin office copy",
    titleSize: 11,
  });
  y += 6;

  // Detail rows for the tear-off zone.
  doc.setFontSize(10);
  const adminRows = [
    ["Amount", formatAmount(amount)],
    ["Date", formatDate(entryDate)],
    [
      "Reason / notes",
      description?.trim() || "________________________________",
    ],
  ];
  for (const [label, value] of adminRows) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(64, 59, 85);
    doc.text(label, marginX, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(48, 45, 69);
    const lines = doc.splitTextToSize(String(value), contentW - 42);
    doc.text(lines, marginX + 42, y);
    y += Math.max(7, lines.length * 5 + 2);
  }

  y += 2;
  y = drawSectionRule(doc, marginX, pageW, y);

  y = drawAdminCopyPartyRow(doc, {
    marginX,
    contentW,
    startY: y,
    roleLabel: "Teacher",
    personName: teacherName,
  });

  y = drawAdminCopyPartyRow(doc, {
    marginX,
    contentW,
    startY: y,
    roleLabel: "Admin",
    personName: adminName,
  });

  drawPdfBrandFooter(doc, { recordLabel, marginX });
}

async function buildDualCopyForm({
  title,
  recordLabel,
  filenamePrefix,
  teacherName,
  adminName,
  amount,
  entryDate,
  description,
  detailRows,
  teacherDeclaration,
  adminDeclaration,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 16;
  const contentW = pageW - marginX * 2;

  // Tear-off line a bit higher so admin copy has room for a small logo.
  const cutY = pageH * ADMIN_ZONE_RATIO;
  const teacherFooterY = cutY - 4;

  const { contentStartY, assets: brandAssets } = await beginBrandedPdf(doc, {
    contentGap: 8,
  });
  let y = contentStartY;

  drawTitleWithCopyLabel(doc, {
    marginX,
    pageW,
    y,
    title,
    copyLabel: "Teacher's copy",
    titleSize: 12.5,
  });

  y += 6;
  y = drawSectionRule(doc, marginX, pageW, y);

  y = drawDetailRows(doc, marginX, contentW, y, detailRows, 7.2);

  y += 2;
  y = drawSectionRule(doc, marginX, pageW, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(48, 45, 69);
  doc.text("Teacher declaration", marginX, y);
  y += 5;

  y = drawWrappedParagraph(
    doc,
    marginX,
    contentW,
    y,
    teacherDeclaration,
  );

  y += 2;
  y = drawSignatureBlock(doc, {
    marginX,
    contentW,
    pageW,
    startY: y,
    leftLabel: "Teacher signature",
    rightLabel: "Date signed",
    lineGap: 10,
  });

  y += 2;
  y = drawSectionRule(doc, marginX, pageW, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(48, 45, 69);
  doc.text("Admin declaration", marginX, y);
  y += 5;

  y = drawWrappedParagraph(
    doc,
    marginX,
    contentW,
    y,
    adminDeclaration,
  );

  y += 2;
  drawSignatureBlock(doc, {
    marginX,
    contentW,
    pageW,
    startY: y,
    leftLabel: "Admin signature",
    rightLabel: "Date signed",
    lineGap: 10,
  });

  // Footer for the teacher's tear-away portion (above the cut line).
  drawPdfBrandFooter(doc, {
    recordLabel,
    marginX,
    footerY: teacherFooterY,
  });

  const adminStartY = drawCutSeparator(doc, marginX, pageW, cutY);

  drawAdminOfficeCopy(doc, {
    marginX,
    contentW,
    pageW,
    startY: adminStartY,
    brandAssets,
    title,
    teacherName,
    adminName,
    amount,
    entryDate,
    description,
    recordLabel,
  });

  doc.save(`${filenamePrefix}_${safeFilenamePart(teacherName)}.pdf`);
}

function safeFilenamePart(value) {
  return String(value || "teacher")
    .trim()
    .replace(/[^\w-]+/g, "_")
    .slice(0, 40);
}

/**
 * Generate and download a printable teacher payout receiving form
 * (teacher copy with dual declarations + tear-off admin office copy).
 */
export async function downloadPayoutReceivingForm({
  teacherName = "",
  adminName = "",
  amount = "",
  entryDate = "",
  description = "",
} = {}) {
  const teacherForDeclaration = teacherName || "________________________";
  const adminForDeclaration = adminName || "________________________";

  await buildDualCopyForm({
    title: "Payment Receiving Confirmation",
    recordLabel: "payout records",
    filenamePrefix: "payout_receiving_form",
    teacherName,
    adminName,
    amount,
    entryDate,
    description,
    detailRows: [
      ["Teacher name", teacherName || "________________________________"],
      ["Amount received", formatAmount(amount)],
      ["Date of receipt", formatDate(entryDate)],
      [
        "Reference / notes",
        description?.trim() || "________________________________",
      ],
    ],
    teacherDeclaration:
      `I, ${teacherForDeclaration}, confirm that I have received the amount of ` +
      `${formatAmount(amount)} from Zainematics on ${formatDate(entryDate)}. ` +
      `I acknowledge this payment as full settlement of the payout described above, ` +
      `and I have no further claim for this specific payout entry.`,
    adminDeclaration:
      `I, ${adminForDeclaration}, confirm that I have paid / handed over the amount of ` +
      `${formatAmount(amount)} to the teacher named above on ${formatDate(entryDate)}, ` +
      `and that this payout has been recorded against the teacher's Zainematics account as described.`,
  });
}

/**
 * Generate and download a printable teacher credit confirmation form
 * (teacher copy with dual declarations + tear-off admin office copy).
 */
export async function downloadCreditConfirmationForm({
  teacherName = "",
  adminName = "",
  amount = "",
  entryDate = "",
  description = "",
} = {}) {
  const teacherForDeclaration = teacherName || "________________________";
  const adminForDeclaration = adminName || "________________________";

  await buildDualCopyForm({
    title: "Account Credit Confirmation",
    recordLabel: "credit records",
    filenamePrefix: "credit_confirmation_form",
    teacherName,
    adminName,
    amount,
    entryDate,
    description,
    detailRows: [
      ["Teacher name", teacherName || "________________________________"],
      ["Amount to credit", formatAmount(amount)],
      ["Credit date", formatDate(entryDate)],
      [
        "Reference / notes",
        description?.trim() || "________________________________",
      ],
    ],
    teacherDeclaration:
      `I, ${teacherForDeclaration}, confirm that I am adding / depositing the amount of ` +
      `${formatAmount(amount)} to my Zainematics teacher account on ${formatDate(entryDate)}. ` +
      `I acknowledge this credit entry and confirm the details above are correct.`,
    adminDeclaration:
      `I, ${adminForDeclaration}, confirm that I have received / taken the amount of ` +
      `${formatAmount(amount)} from the teacher named above on ${formatDate(entryDate)}, ` +
      `and that this amount will be credited to the teacher's Zainematics account as described.`,
  });
}
